import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContactTopic, PlanId } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
    ChevronDown,
    CircleCheck,
    LoaderCircle,
    Send,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/components/generals/button'

import {
    CONTACT_LIMITS,
    CONTACT_TOPICS,
    PLAN_NAMES,
    TEAM_SIZES
} from '@/constants/contact'

type FieldName = 'name' | 'email' | 'company' | 'teamSize' | 'topic' | 'message'

type FieldErrors = Partial<Record<FieldName, string>>

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    company: string
    teamSize: string
    topic: ContactTopic
    message: string
    website: string
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    children: ReactNode
}

interface SubmitResponse {
    ok?: boolean
    message?: string
    errors?: FieldErrors
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const FIELD_ORDER: Array<FieldName> = ['name', 'email', 'company', 'teamSize', 'message']

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    company: '',
    teamSize: '',
    topic: 'demo',
    message: '',
    website: ''
}

const isTopic = (value: unknown): value is ContactTopic => CONTACT_TOPICS.some(topic => topic.value === value)

const isPlan = (value: unknown): value is PlanId => typeof value === 'string' && Object.hasOwn(PLAN_NAMES, value)

const firstValue = (value: string | Array<string> | undefined): string | undefined => Array.isArray(value) ? value[0] : value

const inputClasses = (error?: string): string => cn(
    'w-full rounded-lg border bg-white/85 px-3.5 text-[15px] text-foreground transition-[border-color,box-shadow] placeholder:text-pebble focus:border-primary focus:ring-2 focus:ring-ring/30 focus:outline-none',
    error ? 'border-destructive' : 'border-input'
)

const Field: FC<FieldProps> = ({ id, label, optional = false, error, children }): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-medium text-foreground'>
                {label}

                {optional && (
                    <span className='text-xs font-normal text-mist'>
                        Optional
                    </span>
                )}
            </label>

            <div className='mt-1.5'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-1.5 text-[13px] text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export const ContactForm: FC = (): ReactNode => {
    const router = useRouter()
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [plan, setPlan] = useState<PlanId | null>(null)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [sentName, setSentName] = useState('')

    useEffect(() => {
        if (!router.isReady) return

        const topic = firstValue(router.query.topic)
        const planQuery = firstValue(router.query.plan)

        if (isTopic(topic)) setValues(current => ({ ...current, topic }))

        if (isPlan(planQuery)) {
            setPlan(planQuery)
            setValues(current => ({
                ...current,
                topic: 'pricing',
                message: current.message || `We're interested in the ${PLAN_NAMES[planQuery]} plan.`
            }))
        }
    }, [router.isReady, router.query])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))
        setErrors(current => (current[field as FieldName] ? { ...current, [field]: undefined } : current))
    }

    const focusFirstError = (fieldErrors: FieldErrors) => {
        const first = FIELD_ORDER.find(field => fieldErrors[field])

        if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
    }

    const validate = (): FieldErrors => {
        const next: FieldErrors = {}
        const email = values.email.trim()

        if (!values.name.trim()) next.name = 'Please tell us your name.'

        if (!email) next.email = 'Please add your email so we can reply.'
        else if (!EMAIL_PATTERN.test(email)) next.email = 'That email address does not look right.'

        if (values.message.length > CONTACT_LIMITS.message) next.message = `Please keep your message under ${CONTACT_LIMITS.message} characters.`

        return next
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const clientErrors = validate()

        if (Object.keys(clientErrors).length > 0) {
            setErrors(clientErrors)
            setServerMessage('')
            setStatus('idle')
            focusFirstError(clientErrors)

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, plan })
            })

            const payload = await response.json().catch(() => null) as SubmitResponse | null

            if (response.ok && payload?.ok) {
                setSentName(values.name.trim().split(/\s+/)[0] || '')
                setValues(INITIAL_VALUES)
                setPlan(null)
                setErrors({})
                setStatus('success')

                return
            }

            if (payload?.errors) {
                setErrors(payload.errors)
                focusFirstError(payload.errors)
            }

            setServerMessage(payload?.message || 'Something went wrong on our side. Please try again in a moment.')
            setStatus('error')
        }

        catch {
            setServerMessage('We could not reach the server. Check your connection and try again.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='py-10 text-center animate-in fade-in zoom-in-95 duration-500 focus:outline-none'
            >
                <span className='mx-auto flex size-14 items-center justify-center rounded-full bg-[#e9eee2] text-leaf'>
                    <CircleCheck className='size-7' />
                </span>

                <h2 className='mt-5 font-heading text-[2.6rem] leading-none tracking-[-0.02em] text-foreground'>
                    Thank you{sentName ? `, ${sentName}` : ''}.
                </h2>

                <p className='mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground'>
                    Your note is on its way to our team. We will reply by email, usually within one business day.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className={buttonStyles({ variant: 'light', className: 'mt-7' })}
                >
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form ref={formRef} noValidate onSubmit={submit} className='space-y-5'>
            <fieldset>
                <legend className='text-sm font-medium text-foreground'>
                    What would you like to talk about?
                </legend>

                <div className='mt-2.5 flex flex-wrap gap-2'>
                    {CONTACT_TOPICS.map(topic => (
                        <label key={topic.value} className='cursor-pointer'>
                            <input
                                type='radio'
                                name='topic'
                                value={topic.value}
                                checked={values.topic === topic.value}
                                onChange={update('topic')}
                                className='peer sr-only'
                            />

                            <span className='inline-flex h-10 items-center rounded-full border border-border bg-white/70 px-4 text-sm text-foreground/80 transition-colors peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 hover:border-primary/40'>
                                {topic.label}
                            </span>
                        </label>
                    ))}
                </div>
            </fieldset>

            {plan && (
                <p className='flex items-center justify-between gap-3 rounded-lg border border-primary/25 bg-primary/[0.06] py-2 pr-2 pl-3.5 text-sm text-foreground'>
                    <span>
                        Plan you picked: <strong className='font-semibold'>{PLAN_NAMES[plan]}</strong>
                    </span>

                    <button
                        type='button'
                        onClick={() => setPlan(null)}
                        className='inline-flex size-8 items-center justify-center rounded-md text-mist transition-colors hover:bg-black/5 hover:text-foreground'
                    >
                        <X className='size-4' />
                        <span className='sr-only'>Remove the plan</span>
                    </button>
                </p>
            )}

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='contact-name' label='Your name' error={errors.name}>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        maxLength={CONTACT_LIMITS.name}
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        placeholder='Maya Chen'
                        className={cn(inputClasses(errors.name), 'h-12')}
                    />
                </Field>

                <Field id='contact-email' label='Work email' error={errors.email}>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        maxLength={CONTACT_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        placeholder='maya@yourbrokerage.com'
                        className={cn(inputClasses(errors.email), 'h-12')}
                    />
                </Field>
            </div>

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='contact-company' label='Brokerage or team' optional error={errors.company}>
                    <input
                        id='contact-company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        maxLength={CONTACT_LIMITS.company}
                        value={values.company}
                        onChange={update('company')}
                        aria-invalid={Boolean(errors.company)}
                        aria-describedby={errors.company ? 'contact-company-error' : undefined}
                        className={cn(inputClasses(errors.company), 'h-12')}
                    />
                </Field>

                <Field id='contact-team-size' label='Team size' optional error={errors.teamSize}>
                    <div className='relative'>
                        <select
                            id='contact-team-size'
                            name='teamSize'
                            value={values.teamSize}
                            onChange={update('teamSize')}
                            aria-invalid={Boolean(errors.teamSize)}
                            aria-describedby={errors.teamSize ? 'contact-team-size-error' : undefined}
                            className={cn(inputClasses(errors.teamSize), 'h-12 appearance-none pr-10', !values.teamSize && 'text-mist')}
                        >
                            <option value=''>Choose one</option>

                            {TEAM_SIZES.map(size => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>

                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-mist' />
                    </div>
                </Field>
            </div>

            <Field id='contact-message' label='Anything we should know?' optional error={errors.message}>
                <textarea
                    id='contact-message'
                    name='message'
                    rows={4}
                    maxLength={CONTACT_LIMITS.message}
                    value={values.message}
                    onChange={update('message')}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    placeholder='Which state you work in, how many deals you have open, what slows you down today.'
                    className={cn(inputClasses(errors.message), 'resize-y py-3 leading-relaxed')}
                />
            </Field>

            <div aria-hidden='true' className='hidden'>
                <label htmlFor='contact-website'>Website</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {status === 'error' && serverMessage && (
                <p role='alert' className='rounded-lg border border-destructive/30 bg-destructive/[0.06] px-4 py-3 text-sm text-destructive'>
                    {serverMessage}
                </p>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className={buttonStyles({ size: 'lg', className: 'w-full' })}
            >
                {status === 'submitting' ? (
                    <>
                        <LoaderCircle className='size-4 animate-spin' />
                        Sending
                    </>
                ) : (
                    <>
                        <Send className='size-4' />
                        Send request
                    </>
                )}
            </button>

            <p className='text-center text-xs leading-relaxed text-mist'>
                We only use your details to reply. Read our{' '}
                <Link href='/privacy' className='underline underline-offset-2 hover:text-foreground'>
                    privacy notice
                </Link>
                .
            </p>
        </form>
    )
}
